/* RESPONSIBLE TEAM: team-ai-insights */

import { get } from 'embercom/lib/ajax';
import type Store from '@ember-data/store';
import type TaskSuggestion from 'embercom/models/content-service/task-suggestion';

export enum TaskSuggestionStatus {
  Pending = 0,
  Approved = 1,
  Rejected = 2,
}

export interface TaskSuggestionWireFormat {
  id: number;
  app_id: string;
  success: boolean;
  failure_reason: string;
  suggestion_type: string;
  status: TaskSuggestionStatus;
  created_by_id: string | null;
  reviewer_id: string | null;
  status_updated_at: string | null;
  action_name: string;
  action_description: string;
  action_summary: string;
  action_path: string;
  action_method: string;
  guidance_description: string;
  guidance_category: string;
  implementation_difficulty_score: number;
  implementation_difficulty_reasoning: string;
  conversation_count: number;
  date_range: string;
  confidence_summary: string;
  topic_name_list: string[];
  subtopics: string[];
  action_params: Record<string, any>;
  mock_response: Record<string, any>;
  example_queries: string[];
  conversation_ids: string[];
  sampled_conversation_ids: string[];
  created_at: string;
  updated_at: string;
}

export async function fetchTaskSuggestions(
  store: Store,
  appId: any,
  useSyntheticData = false,
  signal?: AbortSignal,
): Promise<TaskSuggestion[]> {
  try {
    // Clear any existing task suggestions from store to avoid conflicts when switching modes
    store.unloadAll('content-service/task-suggestion');

    let response = (await get(
      '/ember/ai_insights/task_suggestions',
      {
        app_id: appId,
        ...(useSyntheticData ? { synthetic_data_use_case: 'default' } : {}),
      },
      { signal },
    )) as TaskSuggestionWireFormat[];

    if (response.length === 0) {
      return [];
    }

    // Transform the API response into Ember Data format and push to store
    let models = response.map((suggestion) => {
      return store.push({
        data: {
          type: 'content-service/task-suggestion',
          id: suggestion.id || Math.random().toString(36).substring(2, 15),
          attributes: {
            success: suggestion.success,
            failureReason: suggestion.failure_reason,
            suggestionType: suggestion.suggestion_type,
            status: suggestion.status,
            statusUpdatedAt: suggestion.status_updated_at,
            actionName: suggestion.action_name,
            actionDescription: suggestion.action_description,
            actionSummary: suggestion.action_summary,
            actionPath: suggestion.action_path,
            actionMethod: suggestion.action_method,
            guidanceDescription: suggestion.guidance_description,
            guidanceCategory: suggestion.guidance_category,
            conversationCount: suggestion.conversation_count,
            dateRange: suggestion.date_range,
            confidenceSummary: suggestion.confidence_summary,
            implementationDifficultyScore: suggestion.implementation_difficulty_score,
            implementationDifficultyReasoning: suggestion.implementation_difficulty_reasoning,
            suggestionMetadata: {
              topic_name_list: suggestion.topic_name_list,
              subtopics: suggestion.subtopics,
              action_params: suggestion.action_params,
              mock_response: suggestion.mock_response,
              example_queries: suggestion.example_queries,
              conversation_ids: suggestion.conversation_ids,
              sampled_conversation_ids: suggestion.sampled_conversation_ids,
            },
            createdAt: suggestion.created_at,
            updatedAt: suggestion.updated_at,
          },
          relationships: {
            app: {
              data: { type: 'app', id: suggestion.app_id },
            },
            createdBy: suggestion.created_by_id
              ? { data: { type: 'admin', id: suggestion.created_by_id } }
              : { data: null },
            reviewer: suggestion.reviewer_id
              ? { data: { type: 'admin', id: suggestion.reviewer_id } }
              : { data: null },
          },
        },
      }) as TaskSuggestion;
    });

    return [...models];
  } catch (error) {
    console.error('fetchTaskSuggestions error:', error);
    return [];
  }
}
